const normalize = (value) => value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
const controllers = new WeakMap();

function setupForm(root) {
  if (root.dataset.searchReady) return;
  root.dataset.searchReady = 'true';
  const form = root.querySelector('form');
  const input = form?.querySelector('[name="collection_query"]');
  const clear = root.querySelector('[data-collection-search-clear]');
  if (!form || !input) return;
  input.value = new URL(location.href).searchParams.get('collection_query') || '';
  if (clear) clear.hidden = !input.value;
  const navigate = (value) => {
    const url = new URL(location.href);
    if (url.pathname !== new URL(form.action).pathname) return;
    url.searchParams.delete('page');
    url.searchParams.delete('q');
    url.searchParams.delete('collection_id');
    if (value.trim()) url.searchParams.set('collection_query', value.trim());
    else url.searchParams.delete('collection_query');
    url.hash = '';
    location.assign(url.toString());
  };
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    navigate(input.value);
  });
  clear?.addEventListener('click', () => navigate(''));
}

class CollectionSearch {
  constructor(root) {
    this.root = root;
    this.version = 0;
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-last-page'] });
    this.schedule();
  }

  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.run(), 50);
  }

  async run() {
    const root = this.root;
    if (!root.isConnected) {
      this.observer.disconnect();
      this.abort?.abort();
      return;
    }
    const url = new URL(location.href);
    const term = url.searchParams.get('collection_query')?.trim();
    if (!term) return;
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.hash = '';
    const key = url.toString();
    const region = root.querySelector('#ResultsList');
    if (!region || region.dataset.collectionSearchKey === key || (this.pendingKey === key && this.pendingRegion === region)) return;
    this.abort?.abort();
    const abort = new AbortController();
    this.abort = abort;
    const version = ++this.version;
    this.pendingKey = key;
    this.pendingRegion = region;
    root.infinityScrollObserver?.disconnect();
    region.style.visibility = 'hidden';
    region.setAttribute('aria-busy', 'true');
    let status = root.querySelector('[data-collection-search-status]');
    if (!status) {
      status = document.createElement('p');
      status.dataset.collectionSearchStatus = '';
      status.setAttribute('role', 'status');
      status.style.gridColumn = '1 / -1';
      region.before(status);
    }
    status.textContent = root.dataset.searchLoading;
    const words = normalize(term).split(' ').filter(Boolean);
    const fetchPage = async (page) => {
      const pageURL = new URL(url);
      pageURL.searchParams.delete('collection_query');
      pageURL.searchParams.set('section_id', root.getAttribute('section-id'));
      pageURL.searchParams.set('page', String(page));
      const response = await fetch(pageURL, { signal: abort.signal });
      if (!response.ok) throw new Error('Collection response failed');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const list = parsed.querySelector('results-list[data-collection-handle]');
      if (!list || list.dataset.collectionHandle !== root.dataset.collectionHandle) throw new Error('Collection mismatch');
      return list;
    };
    try {
      const first = await fetchPage(1);
      const firstGrid = first.querySelector('[ref="grid"]');
      const pages = firstGrid ? Number(firstGrid.dataset.lastPage) : 1;
      if (!Number.isInteger(pages) || pages < 1) throw new Error('Invalid pagination');
      const results = new Array(pages);
      results[0] = first;
      let next = 2;
      await Promise.all(Array.from({ length: Math.min(3, pages - 1) }, async () => {
        while (next <= pages) {
          const page = next++;
          results[page - 1] = await fetchPage(page);
        }
      }));
      if (version !== this.version || !root.isConnected || region !== root.querySelector('#ResultsList')) return;
      const seen = new Set();
      const cards = [];
      for (const result of results) {
        for (const card of result.querySelectorAll('[data-product-id][data-collection-search-text]')) {
          const text = normalize(card.dataset.collectionSearchText);
          if (words.length && words.every((word) => text.includes(word)) && !seen.has(card.dataset.productId)) {
            seen.add(card.dataset.productId);
            cards.push(card);
          }
        }
      }
      const fresh = first.querySelector('#ResultsList');
      if (!fresh) throw new Error('Missing collection grid');
      const grid = fresh.querySelector('[ref="grid"]');
      if (grid) {
        grid.replaceChildren(...cards);
        grid.dataset.lastPage = '1';
        grid.querySelectorAll('[data-page]').forEach((card) => { card.dataset.page = '1'; });
      }
      fresh.querySelectorAll('[ref="viewMoreNext"], [ref="viewMorePrevious"], .pagination').forEach((element) => element.remove());
      region.dataset.collectionSearchKey = key;
      region.replaceChildren(...fresh.childNodes);
      status.textContent = cards.length ? root.dataset.searchCount.replace('__COUNT__', String(cards.length)) : root.dataset.searchEmpty;
      root.querySelectorAll('.products-count-wrapper > span, .products-count, .facets__product-count').forEach((element) => { element.textContent = root.dataset.searchCount.replace('__COUNT__', String(cards.length)); });
      if (!cards.length) region.replaceChildren();
      region.style.visibility = '';
      region.removeAttribute('aria-busy');
    } catch (error) {
      if (error.name === 'AbortError' || version !== this.version) return;
      status.textContent = root.dataset.searchError;
      region.dataset.collectionSearchKey = key;
      region.replaceChildren();
      region.style.visibility = '';
      region.removeAttribute('aria-busy');
    } finally {
      if (version === this.version) this.pendingKey = null;
    }
  }
}

function initialize() {
  document.querySelectorAll('[data-collection-search]').forEach(setupForm);
  document.querySelectorAll('results-list[data-collection-handle]').forEach((root) => {
    if (!controllers.has(root)) controllers.set(root, new CollectionSearch(root));
  });
}

initialize();
const observer = new MutationObserver(initialize);
observer.observe(document.body, { childList: true, subtree: true });
window.addEventListener('popstate', () => {
  document.querySelectorAll('results-list[data-collection-handle]').forEach((root) => controllers.get(root)?.schedule());
});
